/**
 * Reading a bulk-send list from a CSV.
 *
 * Accepts the shapes people actually export: a header row naming the columns
 * (`Name` / `Full name`, or `First name` + `Last name`, and any column whose
 * name contains "email" — `Email`, `E-mail`, `Work email` … — in any order and
 * case), or no header at all, with the email in whichever column holds one.
 * Every row that cannot be used is counted and reported rather than silently
 * dropped, so the sender can see that 480 of their 500 rows came through and
 * why the other 20 did not.
 *
 * Parsing happens in the browser; the server re-validates every entry (Zod +
 * `normalizeBulkRecipients`), so nothing here is a security boundary.
 */

import Papa from 'papaparse';
import {
  DOCUMENT_SIGN_RECIPIENT_EMAIL_MAX_LENGTH as EMAIL_MAX,
  DOCUMENT_SIGN_RECIPIENT_NAME_MAX_LENGTH as NAME_MAX,
  type DocumentSignBulkRecipient,
} from '@/lib/api';
import { isRecipientEmail } from './emailAddress';

/** Larger than any 500-row list needs; refuses a wrong file before parsing it. */
export const BULK_CSV_MAX_BYTES = 2 * 1024 * 1024;

export interface BulkCsvResult {
  /** New people, in file order — duplicates of each other and of `existing` removed. */
  people: DocumentSignBulkRecipient[];
  /** Rows with no usable email (missing or malformed). */
  invalid: number;
  /** Rows whose email was already on the list, or earlier in the file. */
  duplicates: number;
  /** Valid new rows left out because the list would pass `limit`. */
  overLimit: number;
}

const norm = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '');

function findColumns(header: string[]): {
  email: number;
  name: number;
  first: number;
  last: number;
} | null {
  const keys = header.map(norm);
  // Exact names first, so "Email" wins over "Email verified" when both exist.
  let email = keys.findIndex((k) => k === 'email' || k === 'emailaddress' || k === 'mail');
  if (email === -1) email = keys.findIndex((k) => k.includes('email'));
  if (email === -1) return null;
  const name = keys.findIndex((k) => k === 'name' || k === 'fullname' || k === 'signer');
  const first = keys.findIndex((k) => k === 'firstname' || k === 'givenname' || k === 'first');
  const last = keys.findIndex(
    (k) => k === 'lastname' || k === 'surname' || k === 'familyname' || k === 'last',
  );
  return { email, name, first, last };
}

/**
 * Turn CSV text into people to add to `existing`, at most `limit` in total.
 * `existing` is every row already on the list — including one half-typed
 * (name, no email yet) — since each still takes a place.
 * A row without a name uses its email as the name — the signer is addressed
 * by it, and an empty name would be refused on save.
 */
export function parseBulkRecipientsCsv(
  text: string,
  existing: readonly DocumentSignBulkRecipient[],
  limit: number,
): BulkCsvResult {
  const parsed = Papa.parse<string[]>(text.trim(), { skipEmptyLines: 'greedy' });
  const rows = parsed.data.filter((r) => Array.isArray(r) && r.some((c) => c.trim() !== ''));

  let columns = rows.length > 0 ? findColumns(rows[0]) : null;
  let body = rows;
  if (columns) {
    body = rows.slice(1);
  } else {
    // No recognisable header: the email is whichever column of the first row
    // holds one (email-first files are common), and the name is the first
    // other column, if any.
    const first = rows[0] ?? [];
    const found = first.findIndex((c) => isRecipientEmail(c.trim().toLowerCase()));
    const email = found >= 0 ? found : first.length === 1 ? 0 : 1;
    const name = first.length > 1 ? (email === 0 ? 1 : 0) : -1;
    columns = { email, name, first: -1, last: -1 };
  }

  const seen = new Set(
    existing.map((p) => p.email.trim().toLowerCase()).filter((email) => email !== ''),
  );
  const result: BulkCsvResult = { people: [], invalid: 0, duplicates: 0, overLimit: 0 };
  let room = Math.max(0, limit - existing.length);

  for (const row of body) {
    const email = (row[columns.email] ?? '').trim().toLowerCase();
    if (!email || email.length > EMAIL_MAX || !isRecipientEmail(email)) {
      result.invalid += 1;
      continue;
    }
    if (seen.has(email)) {
      result.duplicates += 1;
      continue;
    }
    if (room === 0) {
      result.overLimit += 1;
      continue;
    }
    const joined =
      columns.name >= 0
        ? (row[columns.name] ?? '')
        : [row[columns.first] ?? '', row[columns.last] ?? ''].join(' ');
    // Control characters are never part of a name (the server strips them too).
    const name =
      joined
        // eslint-disable-next-line no-control-regex -- intentional: strip control bytes from names
        .replace(/[\u0000-\u001f\u007f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, NAME_MAX)
        // A cut that splits a character (an emoji is two UTF-16 units) leaves
        // half of it, which the server's JSON column refuses — drop the half.
        .replace(/[\uD800-\uDBFF]$/, '') || email;
    seen.add(email);
    result.people.push({ name, email });
    room -= 1;
  }
  return result;
}

/** One sentence describing what an import did, for the sender. */
export function describeBulkCsvImport(result: BulkCsvResult, limit: number): string {
  const parts = [
    `Added ${result.people.length} ${result.people.length === 1 ? 'person' : 'people'}.`,
  ];
  if (result.duplicates > 0) parts.push(`${result.duplicates} already on the list.`);
  if (result.invalid > 0) {
    parts.push(
      `${result.invalid} ${result.invalid === 1 ? 'row had' : 'rows had'} no valid email.`,
    );
  }
  if (result.overLimit > 0) {
    parts.push(`${result.overLimit} left out — a bulk send can go to at most ${limit} people.`);
  }
  return parts.join(' ');
}
